import { useCallback, useMemo, useState } from 'react';
import type { Exercise } from '@fluent/types';
import { matchAnswer, type MatchVerdict } from './matchAnswer.js';
import type { LessonAction, LessonState } from './lessonReducer.js';
import { checkTranslateTap, stripPunctuationTokens } from './TranslateTap.js';
import { seededShuffle } from './shuffle.js';
import { playSfx } from './sounds.js';
import { getKnownWords, getNounWords, getVerbWords } from '../content/knownWords.js';

/**
 * The live answer state + grading layer shared by every exercise player
 * (LessonPlayer today). Owns the per-exercise input state, the
 * shuffled tap pool, the submit/verdict logic per exercise type, and the
 * canSubmit gate — so a grading-rule change lands in exactly one place.
 *
 * The reducer (index/phase/results) stays with the caller; this hook only
 * dispatches SUBMIT / ADVANCE_INTRO / MARK_WRONG_ATTEMPT into it.
 */
export interface ExerciseAnswering {
  /** Live text-input answer (shared by translate_typed + listen_type). */
  typedValue: string;
  setTypedValue: (v: string) => void;
  /** Selected indices for the current translate_tap exercise. */
  tapIndices: number[];
  setTapIndices: (next: number[]) => void;
  /** Selected option for choose_correct / listen_choose / cloze / phrase_choice. */
  selectedOption: string | null;
  setSelectedOption: (next: string) => void;
  /** Shuffled token pool for the current translate_tap exercise. */
  tapPool: readonly string[] | null;
  /** Shuffled option order for choose_correct / listen_choose / cloze / phrase_choice. */
  choicePool: readonly string[] | null;
  /** Grade the current exercise and dispatch the verdict. */
  submitCurrent: () => void;
  /** tap_pairs auto-submit callback. */
  onPairsComplete: (summary: { hadWrong: boolean }) => void;
  /** Reset all input state (call alongside NEXT / GO_BACK dispatches). */
  clearAnswers: () => void;
  /** Whether the submit CTA should be enabled for the current input. */
  canSubmit: boolean;
}

/**
 * Display order for a multiple-choice exercise, or null for every other
 * type. Authored order is not a shuffle: across the 240 lessons the answer
 * sits at `options[0]` in ~82% of them, so a learner who never reads past
 * the first card still scores. Seeded by exercise id so the order is stable
 * across re-renders (same contract as tapPool) but differs per exercise.
 */
export function buildChoicePool(
  current: Exercise | undefined,
  retry = false,
): readonly string[] | null {
  if (
    current?.type !== 'choose_correct' &&
    current?.type !== 'listen_choose' &&
    current?.type !== 'cloze' &&
    current?.type !== 'phrase_choice'
  ) {
    return null;
  }
  return seededShuffle(current.options, shuffleSeed(current.id, retry));
}

/** A retry of an earlier mistake gets a fresh order — otherwise the learner
 *  can answer from where the green option sat last time. */
function shuffleSeed(id: string, retry: boolean): string {
  return retry ? `${id}:retry` : id;
}

export function useExerciseAnswering({
  current,
  phase,
  dispatch,
  retry = false,
}: {
  current: Exercise | undefined;
  phase: LessonState['phase'];
  dispatch: (a: LessonAction) => void;
  /** The current exercise is a second chance at an earlier mistake. */
  retry?: boolean;
}): ExerciseAnswering {
  const [typedValue, setTypedValue] = useState('');
  const [tapIndices, setTapIndices] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  /** Stable per exercise.id so the chips don't jump around on re-render. */
  const tapPool = useMemo(() => {
    if (current?.type !== 'translate_tap') return null;
    // Strip pure-punctuation tokens from both sides — sentence-builder
    // exercises don't need the user to tap a trailing period. See
    // TranslateTap.tsx § stripPunctuationTokens for the why.
    return seededShuffle(
      [
        ...stripPunctuationTokens(current.answer_tokens),
        ...stripPunctuationTokens(current.distractors),
      ],
      shuffleSeed(current.id, retry),
    );
  }, [current, retry]);

  /** Stable per exercise.id, same reason as tapPool. */
  const choicePool = useMemo(() => buildChoicePool(current, retry), [current, retry]);

  const submitCurrent = useCallback(() => {
    if (!current || phase !== 'answering') return;

    if (current.type === 'phrase_intro' || current.type === 'dialogue' || current.type === 'grammar_tip') {
      // No-fail teaching cards (grammar tip + single-phrase intro +
      // multi-speaker dialogue): advance straight to the next exercise.
      dispatch({ type: 'ADVANCE_INTRO' });
      return;
    }

    if (current.type === 'translate_typed' || current.type === 'listen_type') {
      if (typedValue.trim() === '') return;
      const verdict: MatchVerdict = matchAnswer({
        input: typedValue,
        answer: current.answer,
        altAnswers: current.alt_answers,
        knownWords: getKnownWords(),
        verbs: getVerbWords(),
        nouns: getNounWords(),
      });
      if (verdict === 'exact') playSfx('correct');
      else if (verdict === 'wrong') playSfx('wrong');
      dispatch({ type: 'SUBMIT', verdict });
      return;
    }

    if (current.type === 'translate_tap' && tapPool) {
      if (tapIndices.length === 0) return;
      const selectedTokens = tapIndices.map((i) => tapPool[i]);
      const verdict = checkTranslateTap(
        selectedTokens,
        stripPunctuationTokens(current.answer_tokens),
      );
      if (verdict === 'exact') playSfx('correct');
      else playSfx('wrong');
      dispatch({ type: 'SUBMIT', verdict });
      return;
    }

    // Multiple-choice family: choose_correct, listen_choose, cloze and the
    // phrases course's phrase_choice. All share the same submit shape —
    // exact match against `answer`.
    if (
      current.type === 'choose_correct' ||
      current.type === 'listen_choose' ||
      current.type === 'cloze' ||
      current.type === 'phrase_choice'
    ) {
      if (selectedOption == null) return;
      const verdict: MatchVerdict = selectedOption === current.answer ? 'exact' : 'wrong';
      if (verdict === 'exact') playSfx('correct');
      else playSfx('wrong');
      dispatch({ type: 'SUBMIT', verdict });
      return;
    }
    // tap_pairs auto-submits via onPairsComplete → no manual Check.
  }, [current, dispatch, phase, selectedOption, tapIndices, tapPool, typedValue]);

  const onPairsComplete = useCallback(
    (summary: { hadWrong: boolean }) => {
      if (phase !== 'answering') return;
      // Completion of every pair = exact verdict, ALWAYS. A mid-exercise
      // wrong tap costs the first-try bonus (via MARK_WRONG_ATTEMPT) but
      // does not flip the binary verdict to 'wrong' — the user demonstrated
      // the final correct match-up, so the lesson should reflect that.
      // Pre-2026-04-29 fix: hadWrong-anywhere → 'wrong', which the user
      // experienced as "I made a mistake, fixed it, completed everything,
      // and was still told I was wrong." Bug confirmed during pilot
      // phone playthrough.
      if (summary.hadWrong) {
        dispatch({ type: 'MARK_WRONG_ATTEMPT' });
      }
      dispatch({ type: 'SUBMIT', verdict: 'exact' });
    },
    [dispatch, phase],
  );

  const clearAnswers = useCallback(() => {
    setTypedValue('');
    setTapIndices([]);
    setSelectedOption(null);
  }, []);

  const canSubmit = (() => {
    if (phase !== 'answering' || !current) return false;
    switch (current.type) {
      case 'grammar_tip':
      case 'phrase_intro':
      case 'dialogue':
        return true; // teaching card — always advanceable
      case 'translate_typed':
      case 'listen_type':
        return typedValue.trim().length > 0;
      case 'translate_tap':
        return tapIndices.length > 0;
      case 'tap_pairs':
        return false; // auto-submits
      case 'choose_correct':
      case 'listen_choose':
      case 'cloze':
      case 'phrase_choice':
        return selectedOption !== null;
    }
  })();

  return {
    typedValue,
    setTypedValue,
    tapIndices,
    setTapIndices,
    selectedOption,
    setSelectedOption,
    tapPool,
    choicePool,
    submitCurrent,
    onPairsComplete,
    clearAnswers,
    canSubmit,
  };
}

/**
 * The reveal text for the result banner, per exercise type. tap_pairs has no
 * single "correct answer" to reveal — every pair is visible on the board.
 *
 * `close` only reaches the banner on the second near-miss (see lessonReducer
 * SUBMIT), and there the one thing worth saying is the exact spelling.
 */
export function resultDetail({
  t,
  lastVerdict,
  current,
}: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  lastVerdict: LessonState['lastVerdict'];
  current: Exercise | undefined;
}): string | null {
  if (lastVerdict === 'exact') return t('lesson.result.correct_detail');
  if (lastVerdict === 'close') {
    if (current?.type === 'translate_typed' || current?.type === 'listen_type') {
      return t('lesson.result.close_detail', { answer: current.answer });
    }
    return null;
  }
  if (lastVerdict !== 'wrong' || !current) return null;
  if (current.type === 'translate_typed' || current.type === 'listen_type') {
    return t('lesson.result.correct_answer', { answer: current.answer });
  }
  if (current.type === 'translate_tap') {
    return t('lesson.result.correct_answer', { answer: current.answer_tokens.join(' ') });
  }
  return null;
}
